<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { minimumImageCost, type ImageBilling } from '~/utils/enterpriseImage'

const props = withDefaults(defineProps<{
  billing: ImageBilling | null
  supportEmail: string
  enterprise?: boolean
}>(), {
  enterprise: false,
})

const { t, locale } = useI18n()
const trigger = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const tooltipContent = ref<HTMLElement>()
const visible = ref(false)
const pinned = ref(false)
const dismissedUntilLeave = ref(false)
const triggerHovered = ref(false)
const tooltipHovered = ref(false)
const tooltipId = `image-billing-tooltip-${useId().replaceAll(':', '')}`
const position = ref({
  top: 0,
  left: 0,
  arrowLeft: 24,
  maxHeight: 560,
  placement: 'below' as 'above' | 'below',
})
let hoverTimer: ReturnType<typeof setTimeout> | undefined
let closeTimer: ReturnType<typeof setTimeout> | undefined

const rtl = computed(() => String(locale.value).toLowerCase().startsWith('ar'))
const numberLocale = computed(() => {
  const code = String(locale.value).toLowerCase()
  if (code === 'zh') return 'zh-CN'
  if (code === 'tw') return 'zh-TW'
  if (code === 'itb') return 'it'
  return code
})
const minimumCost = computed(() => props.billing ? minimumImageCost(props.billing) : 0)

function format(value: number, fractionDigits = 0) {
  return new Intl.NumberFormat(numberLocale.value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: Math.max(fractionDigits, 4),
  }).format(value)
}

function detailParts(key: string, params: Record<string, string>) {
  const parts = String(t(key, params)).split('｜')
  return { label: parts[0] || '', scope: parts[1] || '', value: parts[2] || '' }
}

const rows = computed(() => {
  if (!props.billing) return []
  return [
    detailParts('image_billing_detail_base', { baseCost: format(props.billing.imageTranBaseCost) }),
    detailParts('image_billing_detail_ocr', { ocrCost: format(props.billing.imageTranOCRCostWord) }),
    detailParts('image_billing_detail_generation', { generationCost: format(props.billing.imageTranCostWord) }),
    detailParts('image_billing_detail_multiplier', { ocrMultiplier: format(props.billing.imageTranMagnification, 1) }),
  ]
})

const calculationParams = computed(() => props.billing ? {
  baseCost: format(props.billing.imageTranBaseCost),
  ocrCost: format(props.billing.imageTranOCRCostWord),
  ocrMultiplier: format(props.billing.imageTranMagnification, 1),
  generationCost: format(props.billing.imageTranCostWord),
  minimumCost: format(minimumCost.value),
  twoRunCost: format(minimumCost.value + props.billing.imageTranCostWord),
  threeRunCost: format(minimumCost.value + props.billing.imageTranCostWord * 2),
} : {})

function clearTimers() {
  clearTimeout(hoverTimer)
  clearTimeout(closeTimer)
}

async function updatePosition() {
  if (!visible.value || !trigger.value) return
  await nextTick()
  if (!tooltip.value || !tooltipContent.value) return
  const rect = trigger.value.getBoundingClientRect()
  const width = tooltip.value.offsetWidth || 400
  const viewportPadding = 12
  const gap = 8
  const viewportLeft = viewportPadding
  const viewportRight = window.innerWidth - viewportPadding
  const anchorCenter = rect.left + rect.width / 2
  const left = Math.min(Math.max(anchorCenter - width / 2, viewportLeft), Math.max(viewportLeft, viewportRight - width))
  const fullHeight = tooltipContent.value.scrollHeight
  const availableBelow = Math.max(0, window.innerHeight - rect.bottom - gap - viewportPadding - 2)
  const availableAbove = Math.max(0, rect.top - gap - viewportPadding - 2)
  const placement = availableBelow < Math.min(fullHeight, 320) && availableAbove > availableBelow ? 'above' : 'below'
  const availableHeight = placement === 'above' ? availableAbove : availableBelow
  const maxHeight = Math.max(1, Math.floor(availableHeight))
  const renderedHeight = Math.min(fullHeight, maxHeight)
  position.value = {
    top: placement === 'above'
      ? Math.max(viewportPadding, rect.top - gap - renderedHeight - 2)
      : rect.bottom + gap,
    left,
    arrowLeft: Math.min(Math.max(anchorCenter - left - 7, 18), width - 32),
    maxHeight,
    placement,
  }
}

function open() {
  if (!props.billing) return
  visible.value = true
  void updatePosition()
}

function close() {
  visible.value = false
  pinned.value = false
}

function scheduleOpen() {
  clearTimeout(closeTimer)
  if (dismissedUntilLeave.value || pinned.value || !props.billing) return
  clearTimeout(hoverTimer)
  hoverTimer = setTimeout(open, 300)
}

function scheduleClose() {
  clearTimeout(hoverTimer)
  if (pinned.value) return
  clearTimeout(closeTimer)
  closeTimer = setTimeout(() => {
    if (!triggerHovered.value && !tooltipHovered.value) visible.value = false
  }, 100)
}

function onTriggerEnter() {
  triggerHovered.value = true
  scheduleOpen()
}

function onTriggerLeave() {
  triggerHovered.value = false
  dismissedUntilLeave.value = false
  scheduleClose()
}

function onTooltipEnter() {
  tooltipHovered.value = true
  clearTimeout(closeTimer)
}

function onViewportScroll(event: Event) {
  if (event.target instanceof Node && tooltip.value?.contains(event.target)) return
  void updatePosition()
}

function togglePinned() {
  clearTimers()
  if (!props.billing) return
  if (pinned.value) {
    dismissedUntilLeave.value = true
    close()
    return
  }
  dismissedUntilLeave.value = false
  pinned.value = true
  open()
}

function onDocumentPointerDown(event: PointerEvent) {
  const target = event.target as Node
  if (!visible.value || trigger.value?.contains(target) || tooltip.value?.contains(target)) return
  close()
}

function onDocumentKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && visible.value) {
    close()
    trigger.value?.focus()
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown)
  document.addEventListener('keydown', onDocumentKeyDown)
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', onViewportScroll, true)
})
onBeforeUnmount(() => {
  clearTimers()
  document.removeEventListener('pointerdown', onDocumentPointerDown)
  document.removeEventListener('keydown', onDocumentKeyDown)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onViewportScroll, true)
})
watch(() => props.billing, value => { if (!value) close() })
</script>

<template>
  <span class="image-billing-help">
    <button
      ref="trigger"
      type="button"
      class="image-billing-trigger"
      :class="{ 'is-active': visible }"
      :disabled="!billing"
      :aria-label="$t('image_billing_rule_title')"
      aria-haspopup="dialog"
      :aria-expanded="visible"
      :aria-controls="visible ? tooltipId : undefined"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
      @focus="open"
      @blur="scheduleClose"
      @click="togglePinned"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
        <path d="M12 10.7v5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        <circle cx="12" cy="7.4" r="1.1" fill="currentColor" />
      </svg>
    </button>

    <Teleport to="body">
      <section
        v-if="visible && billing"
        :id="tooltipId"
        ref="tooltip"
        class="image-billing-tooltip"
        :class="{ 'is-above': position.placement === 'above' }"
        :dir="rtl ? 'rtl' : 'ltr'"
        role="dialog"
        :aria-label="$t('image_billing_rule_title')"
        :style="{
          top: `${position.top}px`,
          left: `${position.left}px`,
          '--billing-arrow-left': `${position.arrowLeft}px`,
          '--billing-max-height': `${position.maxHeight}px`,
        }"
        @mouseenter="onTooltipEnter"
        @mouseleave="tooltipHovered = false; scheduleClose()"
        @wheel.stop
        @touchmove.stop
      >
        <div ref="tooltipContent" class="image-billing-tooltip-content">
          <h2>{{ $t('image_billing_rule_title') }}</h2>

          <div class="image-billing-formula">
            <p>{{ $t('image_billing_formula_intro') }}</p>
            <strong>{{ $t('image_billing_formula') }}</strong>
          </div>

          <div class="image-billing-rows">
            <div v-for="row in rows" :key="row.label" class="image-billing-row">
              <strong>{{ row.label }}</strong>
              <small>{{ row.scope }}</small>
              <span dir="ltr">{{ row.value }}</span>
            </div>
          </div>

          <div class="image-billing-minimum">
            <strong>{{ $t('image_billing_minimum_title', calculationParams) }}</strong>
            <p dir="auto">{{ $t('image_billing_minimum_description', calculationParams) }}</p>
          </div>

          <div class="image-billing-explanation">
            <strong>{{ $t('image_billing_limit_question') }}</strong>
            <p>{{ $t('image_billing_limit_answer', calculationParams) }}</p>
            <p>{{ $t('image_billing_charge_note') }}</p>
          </div>

          <div class="image-billing-quality">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 11V9a9 9 0 0 1 18 0v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H3v-7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              <path d="M21 11h-3a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h3v-7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              <path d="M21 18c0 2.2-1.8 4-4 4h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div>
              <strong>{{ $t('image_billing_quality_title') }}</strong>
              <p>{{ $t('image_billing_quality_description', { email: supportEmail }) }}</p>
            </div>
          </div>

          <p class="image-billing-footer">
            {{ $t(enterprise ? 'image_billing_footer_enterprise' : 'image_billing_footer_personal') }}
          </p>
        </div>
      </section>
    </Teleport>
  </span>
</template>

<style scoped>
.image-billing-help { display: inline-flex; flex: 0 0 auto; }
.image-billing-trigger {
  display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; padding: 0;
  border: 0; border-radius: 50%; background: transparent; color: #e5484d; cursor: pointer;
}
.image-billing-trigger svg { width: 15px; height: 15px; }
.image-billing-trigger:hover, .image-billing-trigger:focus-visible, .image-billing-trigger.is-active { color: #c62f35; background: #fdecec; outline: none; }
.image-billing-trigger:disabled { color: #e5484d; opacity: .55; cursor: default; }
.image-billing-tooltip {
  position: fixed; z-index: 3200; width: 400px; max-width: calc(100vw - 24px);
  box-sizing: border-box; border: 1px solid #ebeef2; border-radius: 12px;
  background: #fff; color: #1f2329; box-shadow: 0 10px 30px #0a234d2e; font-family: inherit;
}
.image-billing-tooltip-content {
  display: flex; max-height: var(--billing-max-height, calc(100vh - 24px)); flex-direction: column;
  gap: 12px; padding: 16px 18px; box-sizing: border-box; overflow-y: auto; overscroll-behavior: contain;
  border-radius: 11px; scrollbar-width: thin; scrollbar-color: #cbd2dc transparent;
}
.image-billing-tooltip-content::-webkit-scrollbar { width: 6px; }
.image-billing-tooltip-content::-webkit-scrollbar-thumb { border-radius: 6px; background: #cbd2dc; }
.image-billing-tooltip::before {
  content: ''; position: absolute; top: -7px; left: var(--billing-arrow-left); width: 12px; height: 12px;
  box-sizing: border-box; border: solid #ebeef2; border-width: 1px 0 0 1px; background: #fff; transform: rotate(45deg);
}
.image-billing-tooltip.is-above::before {
  top: auto; bottom: -7px; border-width: 0 1px 1px 0;
}
.image-billing-tooltip h2 { margin: 0; font-size: 14px; font-weight: 700; line-height: 1.6; }
.image-billing-tooltip p { margin: 0; }
.image-billing-formula { display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border-radius: 8px; background: #eaf3fe; }
.image-billing-formula p { color: #51565d; font-size: 11.5px; line-height: 1.6; }
.image-billing-formula strong { color: #2b7de9; font-size: 12.5px; font-weight: 700; line-height: 1.5; }
.image-billing-rows { display: flex; flex-direction: column; }
.image-billing-row { display: flex; align-items: center; gap: 8px; min-height: 37px; padding: 8px 0; border-bottom: 1px solid #eef1f5; }
.image-billing-row:last-child { border-bottom: 0; }
.image-billing-row strong { flex: 0 1 auto; color: #1f2329; font-size: 12.5px; font-weight: 600; line-height: 1.45; }
.image-billing-row small { flex: 0 1 auto; color: #8f959e; font-size: 11px; line-height: 1.45; }
.image-billing-row span { margin-inline-start: auto; flex: 0 0 auto; color: #1f2329; font-size: 12.5px; font-weight: 700; line-height: 1.45; }
.image-billing-minimum { display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border-radius: 8px; background: #fdecec; }
.image-billing-minimum strong { color: #c62f35; font-size: 12.5px; font-weight: 700; line-height: 1.6; }
.image-billing-minimum p { color: #7a2a2e; white-space: pre-line; font-size: 11.5px; line-height: 1.6; }
.image-billing-explanation { display: flex; flex-direction: column; gap: 6px; }
.image-billing-explanation strong { font-size: 12.5px; font-weight: 700; line-height: 1.6; }
.image-billing-explanation p { color: #51565d; font-size: 11.5px; line-height: 1.6; }
.image-billing-quality { display: flex; gap: 8px; padding: 10px 12px; border-radius: 8px; background: #e8f8f0; }
.image-billing-quality > svg { width: 13px; height: 13px; margin-top: 2px; flex: 0 0 13px; color: #0b7a4b; }
.image-billing-quality > div { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.image-billing-quality strong { color: #0b7a4b; font-size: 12.5px; font-weight: 700; line-height: 1.5; }
.image-billing-quality p { color: #0b5c3a; overflow-wrap: anywhere; font-size: 11.5px; line-height: 1.6; }
.image-billing-footer { color: #8f959e; font-size: 11px; line-height: 1.6; }
@media (max-width: 480px) { .image-billing-tooltip { width: calc(100vw - 24px); } }
@media (prefers-reduced-motion: reduce) { .image-billing-trigger { transition: none; } }
</style>
